import { cn } from "@/lib/utils";

import type { ProcedureRow } from "../api/use-procedures";
import type { DecisionOutcome } from "../lib/decision-outcome";
import { OUTCOME_BADGE_CLASSES, OUTCOME_DOT_CLASSES } from "../lib/decision-outcome";
import { formatHearingTypeLabel, PROCEDURE_TYPE_LABELS } from "../lib/procedure-format";

/** House badge anatomy — same as BadgeLegalStep / BadgeClaimType. */
const BADGE_BASE =
  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset";

// biome-ignore-start lint/style/useNamingConvention: enum-valued keys
const procedureTypeClasses: Record<ProcedureRow["procedureType"], string> = {
  PROCEDURE_CLASSIQUE: "bg-slate-50 text-slate-700 ring-slate-600/20",
  ESCP: "bg-cyan-50 text-cyan-700 ring-cyan-600/20",
};
// biome-ignore-end lint/style/useNamingConvention: enum-valued keys

export function BadgeProcedureType({ type }: { type: ProcedureRow["procedureType"] }) {
  return <span className={cn(BADGE_BASE, procedureTypeClasses[type])}>{PROCEDURE_TYPE_LABELS[type]}</span>;
}

/**
 * Tribunal Digital positions, coloured by where the case sits in its lifecycle:
 * amber while live, blue once scheduled, indigo with the judge, violet in
 * deliberation. Unknown values keep a neutral skin rather than disappearing.
 *
 * The closed positions stay neutral on purpose — a judged case can just as well
 * be a rejection, so only the decision itself earns a green or a red.
 */
// biome-ignore-start lint/style/useNamingConvention: keys are Tribunal Digital labels
const positionClasses: Record<string, string> = {
  "Affaire en cours": "bg-amber-50 text-amber-700 ring-amber-600/20",
  Audiencée: "bg-blue-50 text-blue-700 ring-blue-600/20",
  "Juge chargé d'instruire l'affaire": "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  "Au rapport d'un magistrat": "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  "Procédure règlement petit litige": "bg-cyan-50 text-cyan-700 ring-cyan-600/20",
  "En délibéré": "bg-violet-50 text-violet-700 ring-violet-600/20",
  "Affaire en délibéré": "bg-violet-50 text-violet-700 ring-violet-600/20",
  "Délibéré prolongé": "bg-violet-50 text-violet-700 ring-violet-600/20",
};
// biome-ignore-end lint/style/useNamingConvention: keys are Tribunal Digital labels

const NEUTRAL = "bg-gray-50 text-gray-700 ring-gray-600/20";

const positionDotClasses: Record<string, string> = {
  "bg-amber-50 text-amber-700 ring-amber-600/20": "bg-amber-500",
  "bg-blue-50 text-blue-700 ring-blue-600/20": "bg-blue-500",
  "bg-indigo-50 text-indigo-700 ring-indigo-600/20": "bg-indigo-500",
  "bg-cyan-50 text-cyan-700 ring-cyan-600/20": "bg-cyan-500",
  "bg-violet-50 text-violet-700 ring-violet-600/20": "bg-violet-500",
  [NEUTRAL]: "bg-gray-400",
};

/**
 * The European small-claims procedure names its own origin better than any dot:
 * ESCP is the EU regulation, so it carries the flag instead of a colour code.
 */
const ESCP_POSITION = "Procédure règlement petit litige";

export function BadgeProcedurePosition({ position }: { position: string }) {
  const classes = positionClasses[position] ?? NEUTRAL;

  return (
    <span className={cn(BADGE_BASE, classes)}>
      {position === ESCP_POSITION ? (
        <span aria-hidden="true">🇪🇺</span>
      ) : (
        <span className={cn("h-1.5 w-1.5 flex-none rounded-full", positionDotClasses[classes])} />
      )}
      {position}
    </span>
  );
}

/**
 * « Terminée », or « Terminée : <décision> » when the registry says how it ended.
 * Coloured by the outcome, never green by default — « Rejet de la demande » is a
 * loss and must not read as a success.
 */
export function BadgeProcedureClosed({
  decision,
  outcome,
}: {
  decision?: string | null;
  outcome: DecisionOutcome;
}) {
  const label = decision ? `Terminée : ${decision}` : "Terminée";

  return (
    <span className={cn(BADGE_BASE, "max-w-full", OUTCOME_BADGE_CLASSES[outcome])} title={label}>
      <span className={cn("h-1.5 w-1.5 flex-none rounded-full", OUTCOME_DOT_CLASSES[outcome])} />
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Hearing types carry their own colour so a délibéré never looks like a routine
 * mise en état: violet once the court is deciding, indigo for the plaidoirie,
 * amber when someone caused a postponement, slate for routine steps.
 */
// biome-ignore-start lint/style/useNamingConvention: enum-valued keys
const hearingTypeClasses: Record<string, string> = {
  FIRST_HEARING: "bg-blue-50 text-blue-700 ring-blue-600/20",
  PROCEDURAL_HEARING: "bg-slate-50 text-slate-700 ring-slate-600/20",
  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_US: "bg-amber-50 text-amber-700 ring-amber-600/20",
  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_OPPOSING_PARTY:
    "bg-orange-50 text-orange-700 ring-orange-600/20",
  PLEA_HEARING: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  DELIBERATION_HEARING: "bg-violet-50 text-violet-700 ring-violet-600/20",
};
// biome-ignore-end lint/style/useNamingConvention: enum-valued keys

export function BadgeHearingType({ typeName }: { typeName: string }) {
  const label = formatHearingTypeLabel(typeName) ?? typeName;

  return (
    <span className={cn(BADGE_BASE, "max-w-full", hearingTypeClasses[typeName] ?? NEUTRAL)} title={label}>
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Renvoi counter — turns amber past the first one, red on an « ultime renvoi ».
 * The bare number stays compact in a dense row; the title spells it out.
 */
export function BadgeRenvois({ count, ultime }: { count: number; ultime: boolean }) {
  const label = count === 0 ? "Aucun renvoi" : `${count} renvoi${count > 1 ? "s" : ""}`;

  if (count === 0 && !ultime) {
    return (
      <span className="text-xs text-gray-400" title={label}>
        {label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5" title={ultime ? `${label} · ultime` : label}>
      <span
        className={cn(
          BADGE_BASE,
          count > 1
            ? "bg-amber-50 text-amber-700 ring-amber-600/20"
            : "bg-gray-50 text-gray-700 ring-gray-600/20",
        )}
      >
        {label}
      </span>
      {ultime ? (
        <span className={cn(BADGE_BASE, "bg-red-50 text-red-700 ring-red-600/20")}>ultime</span>
      ) : null}
    </span>
  );
}
